// app/src/features/tracking/TrackingSettingsOverlay.jsx
import React from "react";
import { OverlayPanel } from "../../components/Panels.jsx";
import { TrackingSettings } from "./components/TrackingSettings.jsx";
import { listSource } from "../../lib/forecast/listSource.js";

/**
 * The frame's `trackingSettingsOverlay` slot: a forecast's settings flyover (cadence, alerts,
 * access, delete) over the Forecast Manager, for the forecast `trackingSettingsId` names — found
 * in the owner leg first, then the shared lane. Rendered by `AppFrame` as a `TimelineColumn` child
 * after the Forecast Manager; a desktop slot (`PhoneApp` never mounts it).
 */
export default function TrackingSettingsOverlay({ ctx }) {
  const {
    trackingSettingsId,
    forecasts,
    sharedForecasts,
    drawerZ,
    theme,
    getTrackingPrefs,
    patchTrackingPrefs,
    sayerCopy,
    runQuestionAsMine,
    revokeMyAccess,
    mkManageAccess,
    setTrackingSettingsId,
    setDeleteConfirm,
    notifyEmailEnabled,
    leadingBinAlertsEnabled,
    isWatchlisted,
    effectiveToday,
    debugUI,
    debugResolutionOverride,
    active,
  } = ctx;
  return (
    <>
      {/* Closing the log closes this too (via the SideBar onToggle handler, which clears
       trackingSettingsId).

       UNGATED, and deliberately so. The list above renders unconditionally and its cards
       carry the control that opens this, so gating only the panel would leave those
       controls as dead clicks wherever the flag is off, and this panel is the one route
       to DELETE a forecast, which must keep working regardless.

       `forecastTracking` is a DISPLAY gate, not a stop button: it decides what this
       client shows and fetches, never what the server does. See
       `chatboard-forecast-tracking` in cloud/feature-flags for the operator-facing
       statement of what flipping it off does and does not stop. */}
      {(() => {
        // The shared lane too: `forecasts` is the owner leg, and
        // `selectSharedForecasts` is deliberately never merged into it, so a
        // shared row is not findable there. Owner leg first — an id is in one
        // list or the other.
        const fc = trackingSettingsId
          ? (forecasts.find((f) => f.id === trackingSettingsId) ??
            sharedForecasts.find((f) => f.id === trackingSettingsId))
          : null;
        return (
          <OverlayPanel
            side="left"
            open={!!fc}
            // Over the Forecast Manager it opens from, at the sidebar's live width.
            width="var(--rail-panel-w)"
            zOverride={drawerZ.log + 1}
            surface="forecast-settings"
            surfaceKey={fc?.id}
          >
            {fc && (
              <TrackingSettings
                theme={theme}
                // A lean row (one never opened) reads its resolution date and outcome space
                // from the lean summary.
                forecast={listSource(fc)}
                prefs={getTrackingPrefs(fc.id)}
                onPatch={patchTrackingPrefs}
                applySchedule={sayerCopy.applySchedule}
                onRunAsMine={runQuestionAsMine}
                onRevokeMyAccess={revokeMyAccess}
                // onManageAccess is bound to THIS panel's forecast, not the focused one; it is
                // built above because it needs the dialog gate and the telemetry.
                onManageAccess={mkManageAccess(fc)}
                onClose={() => setTrackingSettingsId(null)}
                onDelete={(f) => {
                  setTrackingSettingsId(null);
                  setDeleteConfirm(f);
                }}
                notifyEmailEnabled={notifyEmailEnabled}
                leadingBinAlertsEnabled={leadingBinAlertsEnabled}
                // Tracking settings are settings for tracking: off the
                // watchlist there is no cadence running and no alert to
                // fire, so the panel collapses to ACCESS alone.
                watchlisted={isWatchlisted(fc)}
                nowMs={effectiveToday.getTime()}
                debugUI={debugUI}
                resolutionOverride={
                  debugResolutionOverride && fc.id === active?.id ? debugResolutionOverride : null
                }
              />
            )}
          </OverlayPanel>
        );
      })()}
    </>
  );
}
